﻿﻿﻿<template>
  <div class="panel-card full-height">
    <div class="page-header">
      <h3 class="page-header-title">智能体监控日志</h3>
      <div class="page-header-actions">
        <span class="text-sm text-gray-500">会话对象</span>
        <el-select
          v-model="filters.is_customer_chat"
          size="small"
          class="chat-filter"
          @change="handleCustomerChatFilterChange"
        >
          <el-option label="客户会话" value="1" />
          <el-option label="其他会话" value="0" />
          <el-option label="全部" value="all" />
        </el-select>
        <span class="text-sm text-gray-500">群会话</span>
        <el-select
          v-model="filters.is_group"
          size="small"
          class="group-filter"
          @change="handleGroupFilterChange"
        >
          <el-option label="全部" value="all" />
          <el-option label="群会话" value="1" />
          <el-option label="非群会话" value="0" />
        </el-select>
        <span class="text-sm text-gray-500">消息类型</span>
        <el-select
          v-model="filters.msg_type"
          size="small"
          class="type-filter"
          @change="handleTypeFilterChange"
        >
          <el-option label="全部" value="all" />
          <el-option label="AI回复" value="auto_reply" />
          <el-option label="用户消息" value="user_message" />
        </el-select>
        <span class="text-sm text-gray-500">自动刷新: {{ refreshInterval / 1000 }}s</span>
        <el-button type="primary" size="small" @click="refreshLogs">
          刷新
        </el-button>
      </div>
    </div>

    <div class="table-shell">
      <div class="overflow-x-auto">
        <el-table
          :data="logs"
          style="width: 100%"
          stripe
          height="calc(100% - 40px)"
          :header-cell-style="headerCellStyle"
        >
          <el-table-column prop="created_at" label="发送时间" width="150" show-overflow-tooltip align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column prop="from_user_name" label="发送方" width="100" show-overflow-tooltip>
            <template slot-scope="scope">
              <span :class="scope.row.msg_type === 'auto_reply' ? 'text-blue-600 font-medium' : 'text-gray-800'">
                {{ scope.row.from_user_name }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="recieve_user_name" label="接收方" width="100" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="text-gray-700">{{ scope.row.recieve_user_name }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="ai_representing_user_name" label="AI代答" width="90" show-overflow-tooltip>
            <template slot-scope="scope">
              <span v-if="scope.row.msg_type === 'auto_reply' && scope.row.ai_representing_user_name" class="text-blue-600">
                {{ scope.row.ai_representing_user_name }}
              </span>
              <span v-else class="text-gray-400">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="msg_type" label="消息类型" width="190" align="center">
            <template slot-scope="scope">
              <el-tag :type="messageTypeTagType(scope.row)" size="small">
                {{ messageTypeLabel(scope.row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="main_content" label="消息内容" min-width="500">
            <template slot-scope="scope">
              <div
                :class="[
                  scope.row.msg_type === 'auto_reply'
                    ? 'bg-blue-50 p-2 rounded border-l-4 border-blue-400'
                    : 'bg-gray-50 p-2 rounded border-l-4 border-gray-300',
                  'content-wrap'
                ]">
                <template v-if="isImageMessage(scope.row)">
                  <a
                    v-if="canPreviewImage(scope.row)"
                    class="message-image-link"
                    :href="getImageUrl(scope.row)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      class="message-image"
                      :src="getImageUrl(scope.row)"
                      alt="聊天图片"
                      @error="handleImageLoadError(scope.row)"
                    />
                  </a>
                  <span v-else class="text-gray-500">{{ getImageUnavailableText(scope.row) }}</span>
                </template>
                <template v-else-if="isVoiceMessage(scope.row)">
                  <audio
                    v-if="getVoiceUrl(scope.row)"
                    class="message-audio"
                    controls
                    preload="none"
                    :src="getVoiceUrl(scope.row)"
                  ></audio>
                  <span v-else class="text-gray-500">
                    语音消息（暂无可播放链接）
                    <template v-if="getVoiceDuration(scope.row)">
                      · 时长 {{ getVoiceDuration(scope.row) }}s
                    </template>
                  </span>
                </template>
                <template v-else-if="isFileMessage(scope.row)">
                  <a
                    v-if="getFileUrl(scope.row)"
                    class="message-file-link"
                    :href="getFileUrl(scope.row)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ getFileName(scope.row) || "点击查看文件" }}
                  </a>
                  <span v-else class="text-gray-500">
                    {{ getFileName(scope.row) || "文件消息（暂无可访问链接）" }}
                  </span>
                </template>
                <template v-else-if="isVideoMessage(scope.row)">
                  <video
                    v-if="getVideoUrl(scope.row)"
                    class="message-video"
                    controls
                    preload="metadata"
                    :src="getVideoUrl(scope.row)"
                  ></video>
                  <span v-else class="text-gray-500">
                    视频消息（暂无可播放链接）
                    <template v-if="getVideoDuration(scope.row)">
                      · 时长 {{ getVideoDuration(scope.row) }}s
                    </template>
                  </span>
                </template>
                <template v-else-if="isAppCardMessage(scope.row)">
                  <div class="message-app-card">
                    <div class="message-app-header">
                      <img
                        v-if="getAppCardIconUrl(scope.row)"
                        class="message-app-icon"
                        :src="getAppCardIconUrl(scope.row)"
                        alt="应用图标"
                      />
                      <span class="message-app-name">
                        {{ getAppCardName(scope.row) || messageTypeLabel(scope.row) }}
                      </span>
                    </div>
                    <div class="message-app-title">
                      {{ getAppCardTitle(scope.row) || "应用消息" }}
                    </div>
                    <div v-if="getAppCardDesc(scope.row)" class="message-app-desc">
                      {{ getAppCardDesc(scope.row) }}
                    </div>
                    <a
                      v-if="getAppCardUrl(scope.row)"
                      class="message-file-link"
                      :href="getAppCardUrl(scope.row)"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      打开链接
                    </a>
                    <div v-else-if="getAppCardPath(scope.row)" class="text-gray-500">
                      {{ getAppCardPath(scope.row) }}
                    </div>
                  </div>
                </template>
                <template v-else-if="isCallRecordMessage(scope.row)">
                  <div class="call-record-box">
                    <span class="call-record-title">{{ getCallRecordText(scope.row) }}</span>
                    <span v-if="getCallDuration(scope.row)" class="call-record-sub">时长：{{ getCallDuration(scope.row) }}</span>
                  </div>
                </template>
                <template v-else-if="isReadReceiptMessage(scope.row)">
                  <div class="read-receipt-box">
                    <span class="read-receipt-title">{{ getReadReceiptText(scope.row) }}</span>
                  </div>
                </template>
                <template v-else-if="isRevokeMessage(scope.row)">
                  <div class="revoke-box">
                    <span class="revoke-title">
                      消息已撤回
                      <template v-if="getRevokeOriginalTypeLabel(scope.row)">
                        （原消息：{{ getRevokeOriginalTypeLabel(scope.row) }}）
                      </template>
                    </span>
                    <span v-if="getRevokeInfoLine(scope.row)" class="revoke-sub">
                      {{ getRevokeInfoLine(scope.row) }}
                    </span>
                    <span v-if="getRevokeMsgId(scope.row)" class="revoke-sub">
                      原消息ID：{{ getRevokeMsgId(scope.row) }}
                    </span>
                  </div>
                </template>
                <template v-else-if="isBase64RawMessage(scope.row)">
                  <div class="read-receipt-box">
                    <span class="read-receipt-title">{{ getBase64RawContentText(scope.row) }}</span>
                  </div>
                </template>
                <template v-else-if="isSystemProtocolMessage(scope.row)">
                  <span class="text-gray-500">{{ getSystemProtocolMessageText(scope.row) }}</span>
                </template>
                <template v-else>
                  {{ getPlainContent(scope.row) }}
                </template>
                <div v-if="getAiAnalysis(scope.row)" class="media-ai-analysis">
                  AI分析：{{ getAiAnalysis(scope.row) }}
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="is_customer_chat" label="会话对象" width="90" align="center">
            <template slot-scope="scope">
              <el-tag :type="Number(scope.row.is_customer_chat) === 1 ? 'success' : 'info'" size="small">
                {{ Number(scope.row.is_customer_chat) === 1 ? '客户会话' : '其他会话' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="room_name" label="群会话" width="150" show-overflow-tooltip>
            <template slot-scope="scope">
              <span v-if="scope.row.from_room_id && scope.row.from_room_id != 0" class="text-amber-700">
                {{ scope.row.room_name || scope.row.from_room_id }}
              </span>
              <span v-else class="text-gray-400">-</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-if="!loading && logs.length === 0" class="text-center py-8 text-gray-500">
        暂无日志数据
      </div>
    </div>

    <div class="pagination-container" v-if="pagination.total > 0">
      <el-pagination
        :current-page="pagination.current_page"
        :page-size="pagination.per_page"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "Monitor",
  data() {
    return {
      logs: [],
      refreshInterval: 8000,
      intervalId: null,
      fetchTimerId: null,
      lastFetchAt: 0,
      lastFetchKey: "",
      loading: false,
      imageLoadFailedMap: {},
      filters: {
        is_customer_chat: "1",
        is_group: "0",
        msg_type: "all"
      },
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1
      }
    };
  },
  mounted() {
    this.fetchLogs();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
    this.stopFetchDebounce();
  },
  methods: {
    buildFetchKey() {
      return [
        this.pagination.current_page,
        this.pagination.per_page,
        this.filters.is_customer_chat,
        this.filters.is_group,
        this.filters.msg_type
      ].join("|");
    },
    stopFetchDebounce() {
      if (this.fetchTimerId) {
        clearTimeout(this.fetchTimerId);
        this.fetchTimerId = null;
      }
    },
    scheduleFetchLogs(delay = 220) {
      this.stopFetchDebounce();
      this.fetchTimerId = setTimeout(() => {
        this.fetchLogs();
      }, delay);
    },
    async fetchLogs(options = {}) {
      if (this.loading) return;
      const force = !!(options && options.force);
      const key = this.buildFetchKey();
      const now = Date.now();
      if (!force && key === this.lastFetchKey && now - this.lastFetchAt < 1000) {
        return;
      }
      this.loading = true;
      try {
        // 请求日志数据，带分页参数
        const response = await this.$api.get('/messages/logs', {
          timeout: 60000,
          params: {
            page: this.pagination.current_page,
            per_page: this.pagination.per_page,
            is_customer_chat: this.filters.is_customer_chat,
            is_group: this.filters.is_group,
            msg_type: this.filters.msg_type,
            analyze_images: 0
          }
        });

        const payload = response?.items
          ? response
          : response?.data?.items
            ? response.data
            : response;

        if (Array.isArray(payload)) {
          this.logs = payload;
          this.pagination.total = payload.length;
          return;
        }

        const items = Array.isArray(payload?.items) ? payload.items : [];
        this.logs = items;
        this.imageLoadFailedMap = {};
        this.lastFetchKey = key;
        this.lastFetchAt = Date.now();
        this.pagination = {
          ...this.pagination,
          ...(payload?.pagination || { total: items.length })
        };
      } catch (error) {
        this.logs = [];
        this.pagination.total = 0;
        this.$message.error(error?.message || '获取日志失败');
        console.error('Failed to fetch logs:', error);
      } finally {
        this.loading = false;
      }
    },
    refreshLogs() {
      this.fetchLogs({ force: true });
    },
    handleCustomerChatFilterChange() {
      this.pagination.current_page = 1;
      this.scheduleFetchLogs();
    },
    handleGroupFilterChange() {
      this.pagination.current_page = 1;
      this.scheduleFetchLogs();
    },
    handleTypeFilterChange() {
      this.pagination.current_page = 1;
      this.scheduleFetchLogs();
    },
    handleSizeChange(size) {
      this.pagination.per_page = size;
      this.pagination.current_page = 1;
      this.scheduleFetchLogs();
    },
    handleCurrentChange(current) {
      this.pagination.current_page = current;
      this.scheduleFetchLogs(100);
    },
    startAutoRefresh() {
      this.intervalId = setInterval(() => {
        if (document.hidden || this.loading) return;
        this.fetchLogs();
      }, this.refreshInterval);
    },
    stopAutoRefresh() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    parseJsonDeep(content, maxDepth = 2) {
      let current = content;
      for (let i = 0; i <= maxDepth; i += 1) {
        if (current == null) return null;
        if (typeof current === "object") return current;
        if (typeof current !== "string") return null;

        const trimmed = current.trim();
        if (!trimmed) return null;
        if (!["{", "[", "\""].includes(trimmed[0])) return null;

        try {
          current = JSON.parse(trimmed);
        } catch (e) {
          return null;
        }
      }
      return typeof current === "object" ? current : null;
    },
    parseMainContent(row) {
      return this.parseJsonDeep(row?.main_content) || this.parseMixedJsonObject(row?.main_content);
    },
    parseOriginContent(row) {
      return this.parseJsonDeep(row?.origin_data) || this.parseMixedJsonObject(row?.origin_data);
    },
    getMsgTypeCode(rowOrType) {
      if (rowOrType && typeof rowOrType === "object") {
        const directType = rowOrType.msg_type;
        if (directType !== undefined && directType !== null && String(directType).trim() !== "") {
          return String(directType).trim();
        }

        const origin = this.parseOriginContent(rowOrType);
        const originType = origin?.msgType ?? origin?.msgData?.msgType ?? null;
        if (originType !== undefined && originType !== null && String(originType).trim() !== "") {
          return String(originType).trim();
        }

        return "";
      }

      if (rowOrType === undefined || rowOrType === null) return "";
      return String(rowOrType).trim();
    },
    isKnownMessageType(type) {
      return [
        "auto_reply",
        "0", "2", "4", "6",
        "7", "13", "14", "15", "16", "17", "18", "20", "23", "29", "38", "40", "78",
        "101", "102", "103", "104", "105", "123",
        "1001", "1002", "1006", "1011", "1058",
        "2001", "2005", "2063", "2118", "2120", "2324", "2350",
        "10000", "10002"
      ].includes(String(type));
    },
    isProtocolMessageType(type) {
      return ["517", "539", "2104", "2115", "2120", "2131", "2180", "2215", "2324", "2350"].includes(String(type || "").trim());
    },
    isSystemProtocolMessage(row) {
      return this.isProtocolMessageType(this.getMsgTypeCode(row));
    },
    looksLikeGarbledContent(value) {
      if (value == null) return false;
      const text = String(value).trim();
      if (!text) return false;
      const replacementCount = (text.match(/�/g) || []).length;
      if (replacementCount >= 2) return true;
      const readableChars = (text.match(/[A-Za-z0-9\u4e00-\u9fff，。！？、；：“”‘’（）《》【】,.!?;:'"()\[\]\s_-]/g) || []).length;
      const ratio = readableChars / text.length;
      return ratio < 0.55;
    },
    extractProtocolHint(text) {
      if (!text || typeof text !== "string") return "";
      const matched = text.match(/[A-Za-z0-9_:-]{12,}/g);
      if (!matched || !matched.length) return "";
      return matched[0].slice(0, 64);
    },
    getSystemProtocolMessageText(row) {
      const type = this.getMsgTypeCode(row);
      const structured = this.getStructuredCardSummary(row);
      if (structured) {
        return `系统协议消息（类型${type}）：${structured}`;
      }
      const raw = this.sanitizeDisplayText(row?.main_content || "");
      const hint = this.extractProtocolHint(raw);
      if (hint) {
        return `系统协议消息（类型${type}，标识：${hint}）`;
      }
      return `系统协议消息（类型${type}，原始内容已隐藏）`;
    },
    getBase64RawPayload(row) {
      const mainPayload = this.parseMainContent(row);
      const originPayload = this.parseOriginContent(row);
      const originMsgData = originPayload?.msgData && typeof originPayload.msgData === "object"
        ? originPayload.msgData
        : null;
      const rawMainPayload = this.parseBase64RawPayloadFromString(row?.main_content);
      const rawOriginPayload = this.parseBase64RawPayloadFromString(row?.origin_data);

      const candidates = [mainPayload, originPayload, originMsgData, rawMainPayload, rawOriginPayload];
      for (const payload of candidates) {
        if (!payload || typeof payload !== "object" || Array.isArray(payload)) continue;
        if (payload.base64RawData) return payload;
      }
      return null;
    },
    parseBase64RawPayloadFromString(content) {
      if (typeof content !== "string") return null;
      let raw = content.trim();
      if (!raw || !raw.includes("base64RawData")) return null;

      const parseObject = (value) => {
        if (!value || typeof value !== "string") return null;
        const text = value.trim();
        if (!text) return null;
        if (!text.startsWith("{")) return null;
        try {
          const parsed = JSON.parse(text);
          if (parsed && typeof parsed === "object" && parsed.base64RawData) return parsed;
        } catch (e) {
          return null;
        }
        return null;
      };

      const directParsed = parseObject(raw);
      if (directParsed) return directParsed;

      try {
        const onceParsed = JSON.parse(raw);
        if (onceParsed && typeof onceParsed === "object" && onceParsed.base64RawData) return onceParsed;
        if (typeof onceParsed === "string") {
          const nestedParsed = parseObject(onceParsed);
          if (nestedParsed) return nestedParsed;
          raw = onceParsed;
        }
      } catch (e) {
        // ignore and continue with regex extraction
      }

      const normalized = raw.replace(/\\"/g, "\"");
      const matched = normalized.match(/"base64RawData"\s*:\s*"([^"]+)"/);
      if (!matched || !matched[1]) return null;
      return { base64RawData: matched[1] };
    },
    decodeBase64RawData(base64RawData) {
      if (!base64RawData) return "";
      const value = String(base64RawData).trim();
      if (!value) return "";

      try {
        const compact = value.replace(/\s+/g, "").replace(/-/g, "+").replace(/_/g, "/");
        const remainder = compact.length % 4;
        const padded = remainder === 0 ? compact : compact + "=".repeat(4 - remainder);
        const binary = atob(padded);
        if (typeof TextDecoder !== "undefined") {
          const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
          return new TextDecoder("utf-8").decode(bytes);
        }
        return decodeURIComponent(escape(binary));
      } catch (e) {
        return "";
      }
    },
    sanitizeDisplayText(value) {
      if (value == null) return "";
      let text = String(value);
      if (!text) return "";

      text = text
        .replace(/\r\n/g, "\n")
        .replace(/�+/g, "")
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "")
        .trim();
      if (!text) return "";

      const firstCjkIndex = text.search(/[\u4e00-\u9fff]/);
      if (firstCjkIndex > 0) {
        const prefix = text.slice(0, firstCjkIndex);
        if (prefix.length <= 12 && !/[A-Za-z0-9]{4,}/.test(prefix)) {
          text = text.slice(firstCjkIndex).trim();
        }
      }

      return text;
    },
    extractFirstJsonObjectString(value) {
      if (typeof value !== "string") return "";
      const text = value.trim();
      if (!text) return "";

      const start = text.indexOf("{");
      if (start < 0) return "";

      let depth = 0;
      let inString = false;
      let escaped = false;

      for (let i = start; i < text.length; i += 1) {
        const ch = text[i];
        if (inString) {
          if (escaped) {
            escaped = false;
            continue;
          }
          if (ch === "\\") {
            escaped = true;
            continue;
          }
          if (ch === "\"") {
            inString = false;
          }
          continue;
        }

        if (ch === "\"") {
          inString = true;
          continue;
        }
        if (ch === "{") {
          depth += 1;
          continue;
        }
        if (ch === "}") {
          depth -= 1;
          if (depth === 0) {
            return text.slice(start, i + 1);
          }
        }
      }

      return "";
    },
    parseMixedJsonObject(value) {
      if (typeof value !== "string") return null;
      const jsonText = this.extractFirstJsonObjectString(value);
      if (!jsonText) return null;
      try {
        const parsed = JSON.parse(jsonText);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        return null;
      }
      return null;
    },
    buildCardPayloadSummary(payload) {
      if (!payload || typeof payload !== "object" || Array.isArray(payload)) return "";

      const title = this.sanitizeDisplayText(payload?.main_title?.title || "");
      const subTitle = this.sanitizeDisplayText(payload?.main_title?.sub_title || "");
      const horizonItem = Array.isArray(payload?.horizon_sub_title_list) ? payload.horizon_sub_title_list[0] : null;
      const horizonTitle = this.sanitizeDisplayText(horizonItem?.title || "");
      const horizonSubTitle = this.sanitizeDisplayText(horizonItem?.sub_title || "");
      const horizonText = [horizonTitle, horizonSubTitle].filter(Boolean).join("：");
      const jumpItem = Array.isArray(payload?.jump_list) ? payload.jump_list[0] : null;
      const actionText = this.sanitizeDisplayText(jumpItem?.title || "");

      const parts = [title, subTitle, horizonText, actionText ? `操作：${actionText}` : ""].filter(Boolean);
      if (!parts.length) return "";
      return parts.join("；");
    },
    getStructuredCardSummary(row) {
      const sources = [row?.main_content, row?.origin_data];
      for (const source of sources) {
        if (typeof source !== "string" || !source.trim()) continue;
        const payload = this.parseMixedJsonObject(source);
        if (!payload) continue;
        const summary = this.buildCardPayloadSummary(payload);
        if (summary) return summary;
      }
      return "";
    },
    isLikelyJsonString(value) {
      if (typeof value !== "string") return false;
      const text = value.trim();
      if (!text) return false;
      return text.startsWith("{") || text.startsWith("[");
    },
    getDetailTextFromPayload(payload) {
      if (!payload || typeof payload !== "object" || Array.isArray(payload)) return "";

      const content = this.sanitizeDisplayText(payload.content);
      if (content && !this.isLikelyJsonString(content)) return content;

      const detailText = Array.isArray(payload.moreDetail)
        ? this.sanitizeDisplayText(
            payload.moreDetail
              .map((detail) => {
                if (!detail || typeof detail !== "object") return "";
                return typeof detail.text === "string" ? detail.text : "";
              })
              .filter(Boolean)
              .join("")
          )
        : "";
      if (detailText) return detailText;

      if (payload.reply && typeof payload.reply === "object") {
        const replyContent = this.sanitizeDisplayText(payload.reply.content);
        if (replyContent && !this.isLikelyJsonString(replyContent)) return replyContent;

        const replyDetailText = Array.isArray(payload.reply.moreDetail)
          ? this.sanitizeDisplayText(
              payload.reply.moreDetail
                .map((detail) => {
                  if (!detail || typeof detail !== "object") return "";
                  return typeof detail.text === "string" ? detail.text : "";
                })
                .filter(Boolean)
                .join("")
            )
          : "";
        if (replyDetailText) return replyDetailText;
      }

      return "";
    },
    getPayloadTextCandidate(row) {
      const mainPayload = this.parseMainContent(row);
      const originPayload = this.parseOriginContent(row);
      const candidates = [
        mainPayload,
        mainPayload && typeof mainPayload === "object" ? mainPayload.msgData : null,
        originPayload,
        originPayload && typeof originPayload === "object" ? originPayload.msgData : null
      ];

      for (const candidate of candidates) {
        const text = this.getDetailTextFromPayload(candidate);
        if (text) return text;
      }
      return "";
    },
    getMainContentTextCandidate(row) {
      const content = row?.main_content;
      if (content == null) return "";
      if (typeof content !== "string") return "";

      const text = this.sanitizeDisplayText(content);
      if (!text) return "";
      if (text === "[]" || text === "{}") return "";
      if (this.isLikelyJsonString(text)) return "";
      return text;
    },
    isBase64RawMessage(row) {
      const payload = this.getBase64RawPayload(row);
      if (!payload) return false;
      if (this.isRevokeMessage(row) || this.isCallRecordMessage(row)) return false;

      const mainText = this.getMainContentTextCandidate(row);
      if (mainText) return true;

      const decoded = this.sanitizeDisplayText(this.decodeBase64RawData(payload.base64RawData));
      return !!decoded;
    },
    getBase64RawContentText(row) {
      const payload = this.getBase64RawPayload(row);
      if (!payload) return "";
      if (payload.recordWording) return String(payload.recordWording);
      const mainText = this.getMainContentTextCandidate(row);
      if (mainText) return mainText;
      const decoded = this.decodeBase64RawData(payload.base64RawData);
      const cleaned = this.sanitizeDisplayText(decoded);
      return cleaned || "系统加密消息";
    },
    normalizeMediaUrl(url) {
      if (!url) return "";
      let value = String(url).trim();
      if (!value) return "";

      if (
        (value.startsWith('"') && value.endsWith('"'))
        || (value.startsWith("'") && value.endsWith("'"))
      ) {
        value = value.slice(1, -1).trim();
      }

      value = value.replace(/\\\//g, "/").replace(/&amp;/gi, "&");
      if (value.startsWith("//")) {
        value = `https:${value}`;
      }
      if (/^https?:\/\//i.test(value)) return value;
      const matched = value.match(/https?:\/\/[^\s"'<>]+/i);
      return matched ? matched[0] : "";
    },
    getPlainContent(row) {
      const structured = this.getStructuredCardSummary(row);
      if (structured) return structured;
      if (this.isSystemProtocolMessage(row)) {
        return this.getSystemProtocolMessageText(row);
      }
      if (this.isBase64RawMessage(row)) {
        return this.getBase64RawContentText(row);
      }
      const payloadText = this.getPayloadTextCandidate(row);
      if (payloadText && !this.looksLikeGarbledContent(payloadText)) return payloadText;
      const content = row?.main_content;
      if (content == null) return "";
      if (typeof content === "string") {
        const text = this.sanitizeDisplayText(content);
        if (this.isLikelyJsonString(text)) return "";
        if (this.looksLikeGarbledContent(text)) {
          return this.getSystemProtocolMessageText(row);
        }
        return text;
      }
      try {
        return this.sanitizeDisplayText(JSON.stringify(content));
      } catch (e) {
        return String(content);
      }
    },
    getMediaUrlFromPayload(payload) {
      if (!payload) return "";
      return this.normalizeMediaUrl(
        payload.cloudUrl
        || payload.mediaCloudUrl
        || payload.media_cloud_url
        || payload.fileBigHttpUrl
        || payload.fileMiddleHttpUrl
        || payload.fileThumbHttpUrl
        || payload.fileHttpUrl
        || payload.voiceHttpUrl
        || payload.videoHttpUrl
        || payload.downloadUrl
        || payload.url
        || ""
      );
    },
    getMediaUrlFromOrigin(row) {
      const origin = this.parseOriginContent(row);
      if (!origin) return "";
      const msgData = origin.msgData && typeof origin.msgData === "object"
        ? origin.msgData
        : {};
      return this.getMediaUrlFromPayload(msgData);
    },
    getRawUrl(row) {
      const content = row?.main_content;
      if (typeof content !== "string") return "";
      const trimmed = content.trim();
      const normalized = this.normalizeMediaUrl(trimmed);
      if (normalized) return normalized;

      const matched = trimmed.match(/https?:\/\/[^\s"'<>]+/i);
      return matched ? this.normalizeMediaUrl(matched[0]) : "";
    },
    getRowKey(row) {
      return row?.id || row?.msg_id || row?.msg_timestamp || "";
    },
    hasImageLoadFailed(row) {
      const key = this.getRowKey(row);
      return key ? !!this.imageLoadFailedMap[key] : false;
    },
    handleImageLoadError(row) {
      const key = this.getRowKey(row);
      if (!key) return;
      this.$set(this.imageLoadFailedMap, key, true);
    },
    canPreviewImage(row) {
      return !!this.getImageUrl(row) && !this.hasImageLoadFailed(row);
    },
    getImageUnavailableText(row) {
      if (this.hasImageLoadFailed(row)) {
        return "图片链接已失效或已过期";
      }
      const payload = this.parseMainContent(row) || {};
      const origin = this.parseOriginContent(row);
      const originPayload = origin?.msgData && typeof origin.msgData === "object"
        ? origin.msgData
        : {};
      const fileId = payload.fileId || originPayload.fileId || "";
      if (fileId) return `图片文件ID：${fileId}`;
      return "图片消息（暂无可预览链接）";
    },
    isImageMessage(row) {
      const type = this.getMsgTypeCode(row);
      return ["7", "14", "29", "101", "104"].includes(type);
    },
    getImageUrl(row) {
      return this.getMediaUrl(row);
    },
    isVoiceMessage(row) {
      return this.getMsgTypeCode(row) === "16";
    },
    getVoiceUrl(row) {
      return this.getMediaUrl(row);
    },
    getVoiceDuration(row) {
      const payload = this.parseMainContent(row);
      if (!payload) return "";
      const seconds = Number(payload.voiceTime || payload.duration || 0);
      return Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : "";
    },
    isFileMessage(row) {
      const type = this.getMsgTypeCode(row);
      return type === "15" || type === "17";
    },
    getFileUrl(row) {
      return this.getMediaUrl(row);
    },
    getFileName(row) {
      const payload = this.parseMainContent(row);
      if (!payload) return "";
      return payload.filename || payload.fileName || payload.name || "";
    },
    isVideoMessage(row) {
      const type = this.getMsgTypeCode(row);
      if (type === "20" || type === "102") return true;
      return !!this.getVideoPayload(row);
    },
    getVideoUrl(row) {
      return this.getMediaUrl(row);
    },
    getVideoPayload(row) {
      const payload = this.parseMainContent(row);
      const originPayload = this.parseOriginContent(row);
      const originMsgData = originPayload?.msgData && typeof originPayload.msgData === "object"
        ? originPayload.msgData
        : null;

      const candidates = [payload, originPayload, originMsgData];
      for (const candidate of candidates) {
        if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) continue;

        const hasVideoUrl = !!(
          candidate.cloudUrl
          || candidate.mediaCloudUrl
          || candidate.media_cloud_url
          || candidate.videoHttpUrl
          || candidate.videoUrl
          || candidate.videoUrlHttp
          || candidate.fileBigHttpUrl
          || candidate.fileMiddleHttpUrl
          || candidate.fileHttpUrl
        );
        const hasVideoMeta = (
          candidate.videoWidth !== undefined
          || candidate.videoHeight !== undefined
          || candidate.coverImageId !== undefined
          || candidate.coverImageUrl !== undefined
          || candidate.coverImageHttpUrl !== undefined
        );
        const hasFileIdentity = candidate.fileId !== undefined || candidate.fileMd5 !== undefined;

        if (hasVideoUrl || (hasVideoMeta && hasFileIdentity)) {
          return candidate;
        }
      }
      return null;
    },
    getVideoDuration(row) {
      const payload = this.getVideoPayload(row) || this.parseMainContent(row);
      if (!payload) return "";
      const seconds = Number(payload.duration || payload.videoDuration || 0);
      return Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : "";
    },
    getAppCardPayload(row) {
      const payload = this.parseMainContent(row);
      const originPayload = this.parseOriginContent(row);
      const originMsgData = originPayload?.msgData && typeof originPayload.msgData === "object"
        ? originPayload.msgData
        : null;

      const candidates = [payload, originPayload, originMsgData];
      for (const candidate of candidates) {
        if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) continue;
        if (
          candidate.appId
          || candidate.appName
          || candidate.title
          || candidate.pagePath
          || candidate.username
          || candidate.iconUrl
          || candidate.url
          || candidate.appMediaUrl
          || candidate.desc
        ) {
          return candidate;
        }
      }
      return null;
    },
    isAppCardMessage(row) {
      const type = this.getMsgTypeCode(row);
      if (type === "13" || type === "78") return true;
      return !!this.getAppCardPayload(row);
    },
    getAppCardTitle(row) {
      const payload = this.getAppCardPayload(row);
      if (!payload) return "";
      return payload.title || payload.name || "";
    },
    getAppCardDesc(row) {
      const payload = this.getAppCardPayload(row);
      if (!payload) return "";
      return payload.desc || payload.content || "";
    },
    getAppCardName(row) {
      const payload = this.getAppCardPayload(row);
      if (!payload) return "";
      return payload.appName || payload.username || "";
    },
    getAppCardIconUrl(row) {
      const payload = this.getAppCardPayload(row);
      if (!payload) return "";
      return this.normalizeMediaUrl(
        payload.iconUrl
        || payload.coverImageUrl
        || payload.coverImageHttpUrl
        || payload.coverImageThumbUrl
        || ""
      );
    },
    getAppCardUrl(row) {
      const payload = this.getAppCardPayload(row);
      if (!payload) return "";
      return this.normalizeMediaUrl(
        payload.url
        || payload.appMediaUrl
        || payload.linkUrl
        || ""
      );
    },
    getAppCardPath(row) {
      const payload = this.getAppCardPayload(row);
      if (!payload) return "";
      if (payload.pagePath) return `小程序路径：${payload.pagePath}`;
      if (payload.username) return `小程序账号：${payload.username}`;
      return "";
    },
    getMediaUrl(row) {
      return this.getRawUrl(row)
        || this.getMediaUrlFromPayload(this.parseMainContent(row))
        || this.getMediaUrlFromOrigin(row);
    },
    getAiAnalysis(row) {
      const payload = this.parseMainContent(row);
      const origin = this.parseOriginContent(row);
      const originMsgData = origin?.msgData && typeof origin.msgData === "object"
        ? origin.msgData
        : null;

      const text = (
        payload?.ai_analysis
        || origin?.ai_media_analysis
        || originMsgData?.ai_analysis
        || ""
      );
      return typeof text === "string" ? text.trim() : "";
    },
    isCallRecordMessage(row) {
      const type = this.getMsgTypeCode(row);
      const payload = this.parseMainContent(row);
      return type === "40" || (
        !!payload
        && (payload.recordWording !== undefined || payload.recordType !== undefined || payload.inviteType !== undefined)
      );
    },
    getCallRecordText(row) {
      const payload = this.parseMainContent(row);
      if (!payload) return "通话记录";
      if (payload.recordWording) return String(payload.recordWording);

      const recordType = Number(payload.recordType);
      if (recordType === 2) return "对方已拒绝";
      if (recordType === 3) return "对方未接听";
      if (recordType === 4) return "已取消";
      if (recordType === 5) return "通话已结束";
      return "通话记录";
    },
    getCallDuration(row) {
      const payload = this.parseMainContent(row);
      if (!payload) return "";

      const seconds = Number(payload.duration || 0);
      if (!Number.isFinite(seconds) || seconds <= 0) return "";

      const mm = Math.floor(seconds / 60);
      const ss = seconds % 60;
      return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
    },
    isReadReceiptMessage(row) {
      const type = this.getMsgTypeCode(row);
      return type === "2001" || type === "2005";
    },
    getReadReceiptText(row) {
      const type = this.getMsgTypeCode(row);
      if (type === "2001") return "消息已读通知";
      if (type === "2005") return "消息未读通知";
      return "消息状态通知";
    },
    getRevokePayload(row) {
      const mainPayload = this.parseMainContent(row);
      const originPayload = this.parseOriginContent(row);
      const originMsgData = originPayload?.msgData && typeof originPayload.msgData === "object"
        ? originPayload.msgData
        : null;

      const candidates = [mainPayload, originPayload, originMsgData];
      for (const payload of candidates) {
        if (!payload || typeof payload !== "object" || Array.isArray(payload)) continue;
        if (
          payload.revokeMsgUniqueIdentifier !== undefined
          || payload.revokeUserId !== undefined
          || payload.revokeTime !== undefined
        ) {
          return payload;
        }
      }
      return null;
    },
    isRevokeMessage(row) {
      const type = this.getMsgTypeCode(row);
      if (type === "10000" || type === "10002") return true;
      return !!this.getRevokePayload(row);
    },
    formatUnixTimestamp(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric <= 0) return "";
      const ms = numeric > 1e12 ? numeric : numeric * 1000;
      const date = new Date(ms);
      if (Number.isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      const second = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    getRevokeUserName(row) {
      const payload = this.getRevokePayload(row);
      const revokeUserId = String(payload?.revokeUserId ?? "").trim();
      if (!revokeUserId) return "";

      if (revokeUserId === String(row?.from_user_id ?? "")) {
        return row?.from_user_name || revokeUserId;
      }
      if (revokeUserId === String(row?.recieve_user_id ?? "")) {
        return row?.recieve_user_name || revokeUserId;
      }
      if (revokeUserId === String(row?.ai_representing_user_id ?? "")) {
        return row?.ai_representing_user_name || revokeUserId;
      }
      return revokeUserId;
    },
    getRevokeInfoLine(row) {
      const payload = this.getRevokePayload(row);
      if (!payload) return "";
      const parts = [];
      const userName = this.getRevokeUserName(row);
      const revokeTime = this.formatUnixTimestamp(payload.revokeTime);
      if (userName) parts.push(`撤回人：${userName}`);
      if (revokeTime) parts.push(`撤回时间：${revokeTime}`);
      return parts.join(" · ");
    },
    getRevokeMsgId(row) {
      const payload = this.getRevokePayload(row);
      if (!payload) return "";
      return String(payload.revokeMsgUniqueIdentifier ?? "").trim();
    },
    getRevokeOriginalTypeLabel(row) {
      const payload = this.getRevokePayload(row);
      if (!payload) return "";
      const originalType = payload?.extras?.signedMsg?.originalContentType;
      if (originalType == null) return "";
      return this.messageTypeLabelByCode(originalType);
    },
    isAutoReplySendSuccess(row) {
      return this.getMsgTypeCode(row) === "auto_reply" && Number(row?.ai_reply_send_success) === 1;
    },
    isAutoReply(row) {
      return this.getMsgTypeCode(row) === "auto_reply";
    },
    isAutoReplyNotSent(row) {
      return this.isAutoReply(row) && Number(row?.ai_reply_send_success) !== 1;
    },
    getAutoReplySendStatusText(row) {
      if (!this.isAutoReply(row)) return "";
      const status = String(row?.ai_reply_send_status || "").toLowerCase();
      if (Number(row?.ai_reply_send_success) === 1 || status === "success" || status === "ok" || status === "sent") {
        return "已发微信";
      }
      if (status === "failed" || status === "error") {
        return "发送失败";
      }
      return "未发微信";
    },
    messageTypeLabel(input) {
      if (input && typeof input === "object") {
        if (this.isRevokeMessage(input)) return "撤回消息";
        if (this.isAutoReply(input)) return `AI回复（${this.getAutoReplySendStatusText(input)}）`;
        if (this.isVideoMessage(input)) return "视频消息";
        if (this.isAppCardMessage(input)) return "应用消息";
        return this.messageTypeLabelByCode(this.getMsgTypeCode(input));
      }
      return this.messageTypeLabelByCode(input);
    },
    messageTypeLabelByCode(msgType) {
      const type = this.getMsgTypeCode(msgType);
      if (type === "auto_reply") return "AI回复";
      if (type === "0") return "文本消息";
      if (type === "2") return "文本消息";
      if (type === "13") return "链接消息";
      if (["7", "14", "29", "101", "104"].includes(type)) return "图片消息";
      if (type === "15" || type === "17") return "文件消息";
      if (type === "16") return "语音消息";
      if (type === "18") return "XML消息";
      if (type === "20" || type === "102") return "视频消息";
      if (type === "78") return "小程序消息";
      if (type === "40") return "通话记录";
      if (type === "2001") return "已读通知";
      if (type === "2005") return "未读通知";
      if (this.isProtocolMessageType(type)) return "系统协议消息";
      if (type === "105") return "Markdown消息";
      if (!type) return "未知类型";
      return `类型${type}`;
    },
    messageTypeTagType(input) {
      if (input && typeof input === "object" && this.isRevokeMessage(input)) return "warning";
      if (input && typeof input === "object" && this.isAutoReplySendSuccess(input)) return "success";
      if (input && typeof input === "object" && this.isAutoReplyNotSent(input)) return "danger";

      const type = this.getMsgTypeCode(input && typeof input === "object" ? input : input);
      if (type === "auto_reply") return "primary";
      if (["7", "14", "29", "101", "104"].includes(type)) return "warning";
      if (type === "16") return "success";
      if (type === "40") return "danger";
      if (type === "2001") return "success";
      if (type === "2005") return "warning";
      if (type === "15" || type === "17" || type === "20" || type === "102") return "";
      return "info";
    },
    headerCellStyle() {
      return {
        backgroundColor: '#f8fafc',
        color: '#334155',
        fontWeight: 600,
        fontSize: '13px'
      };
    }
  }
};
</script>

<style scoped>
/* Full height layout */
.full-height {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 130px);
  overflow: hidden;
}

.table-shell {
  flex: 1;
  margin: 8px 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background-color: white;
}

.table-shell .el-table {
  flex: 1;
  min-height: 0;
  height: 100%;
}

/* Ensure the table body takes full height */
.table-shell .el-table__body-wrapper {
  flex: 1;
  min-height: 0;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  margin-bottom: 8px;
}

.page-header-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.page-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.group-filter {
  width: 130px;
}

.chat-filter {
  width: 130px;
}

.type-filter {
  width: 120px;
}

.pagination-container {
  margin-top: 12px;
  padding: 0 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.pagination-container .el-pagination {
  margin: 0;
}

/* Ensure the table is responsive */
.overflow-x-auto {
  flex: 1;
  min-height: 0;
}

.content-wrap {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
}

.media-ai-analysis {
  margin-top: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  background: #eff6ff;
  border-left: 3px solid #3b82f6;
  color: #1e40af;
  font-size: 12px;
}

.message-image-link {
  display: inline-block;
  cursor: pointer;
}

.message-image {
  max-width: 220px;
  max-height: 220px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  object-fit: cover;
}

.message-audio {
  width: 260px;
  max-width: 100%;
}

.message-file-link {
  color: #0369a1;
  text-decoration: underline;
  cursor: pointer;
}

.message-video {
  width: 280px;
  max-width: 100%;
  max-height: 220px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #000;
}

.message-app-card {
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  max-width: 360px;
}

.message-app-header {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.message-app-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  object-fit: cover;
}

.message-app-name {
  color: #475569;
  font-size: 12px;
}

.message-app-title {
  color: #0f172a;
  font-weight: 600;
  line-height: 1.45;
}

.message-app-desc {
  color: #475569;
  font-size: 12px;
  line-height: 1.45;
  word-break: break-word;
}

.call-record-box {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
}

.call-record-title {
  color: #1f2937;
  font-weight: 600;
}

.call-record-sub {
  color: #6b7280;
  font-size: 12px;
}

.read-receipt-box {
  display: inline-flex;
  align-items: center;
  padding: 2px 0;
}

.read-receipt-title {
  color: #374151;
  font-weight: 600;
}

.revoke-box {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
}

.revoke-title {
  color: #7c2d12;
  font-weight: 600;
}

.revoke-sub {
  color: #6b7280;
  font-size: 12px;
}
</style>
